import { useMemo } from "react";
import {
  ReactFlow,
  Background,
  Controls,
  type Node,
  type Edge,
  type NodeTypes,
  Handle,
  Position,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";

type ReactFlowData = {
  nodes: Array<{
    id: string;
    label: string;
    description?: string;
    color?: string;
    x: number;
    y: number;
  }>;
  edges: Array<{
    source: string;
    target: string;
    label?: string;
  }>;
};

function OrgNode({ data }: { data: { label: string; description?: string; color?: string } }) {
  const color = data.color ?? "#64748b";
  return (
    <>
      <Handle type="target" position={Position.Top} className="!bg-transparent !border-0 !w-0 !h-0" />
      <div
        className="rounded-xl border-2 px-4 py-3 min-w-[140px] text-center bg-background shadow-sm"
        style={{ borderColor: color }}
      >
        <div className="text-sm font-semibold text-foreground">{data.label}</div>
        {data.description && (
          <div className="text-[11px] text-muted-foreground mt-0.5">{data.description}</div>
        )}
      </div>
      <Handle type="source" position={Position.Bottom} className="!bg-transparent !border-0 !w-0 !h-0" />
    </>
  );
}

const nodeTypes: NodeTypes = { orgNode: OrgNode };

export function ReactFlowSection({ data }: { data: ReactFlowData }) {
  const nodes: Node[] = useMemo(
    () =>
      (data.nodes ?? []).map((n) => ({
        id: n.id,
        type: "orgNode",
        position: { x: n.x, y: n.y },
        data: { label: n.label, description: n.description, color: n.color },
      })),
    [data.nodes]
  );

  const edges: Edge[] = useMemo(
    () =>
      (data.edges ?? []).map((e, i) => ({
        id: `e-${i}`,
        source: e.source,
        target: e.target,
        label: e.label,
        style: { stroke: "#64748b", strokeWidth: 2 },
        labelStyle: { fill: "#475569", fontSize: 11, fontWeight: 500 },
      })),
    [data.edges]
  );

  return (
    <div className="h-[400px] rounded-lg border border-border bg-muted/30">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        fitView
        proOptions={{ hideAttribution: true }}
        nodesDraggable={false}
        nodesConnectable={false}
        zoomOnScroll={false}
        panOnDrag
      >
        <Background color="hsl(var(--border))" gap={20} />
        <Controls
          showInteractive={false}
          className="!bg-card !border-border !rounded-lg [&_button]:!bg-card [&_button]:!border-border [&_button]:!text-muted-foreground [&_button:hover]:!bg-muted"
        />
      </ReactFlow>
    </div>
  );
}
